import type { ThemeOptions } from "@mui/material/styles";
import type { OperatorChromeTokens } from "./types";

/**
 * Single place for Operator MUI primitive chrome.
 * Both operator-dark and clean-enterprise MUST call this — never fork overrides per theme.
 */
export function buildOperatorMuiComponents(
  t: OperatorChromeTokens,
): NonNullable<ThemeOptions["components"]> {
  return {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: t.bg } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none", border: `1px solid ${t.border}` },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${t.border}`,
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: { variant: "contained", size: "small", disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "6px 14px",
          textTransform: "none",
          fontWeight: 500,
        },
        containedPrimary: {
          boxShadow: `0 8px 20px -8px ${t.accent}80`,
        },
      },
    },
    MuiIconButton: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          fontSize: 13,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
        },
        input: { paddingTop: 8, paddingBottom: 8 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontFamily: t.sans,
            fontSize: 14,
            letterSpacing: "normal",
            textTransform: "none",
            color: t.ink,
            fontWeight: 800,
            borderColor: t.border,
            "& .MuiTableSortLabel-root": {
              color: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              "&:hover": { color: "inherit" },
              "&.Mui-active": { color: "inherit" },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 12.5,
          borderColor: t.border,
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { backgroundColor: `${t.accent}14` },
          "&.Mui-selected:hover": { backgroundColor: `${t.accent}1f` },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20, fontWeight: 500, fontSize: 11 },
        colorSuccess: { backgroundColor: t.successLight, color: t.success },
        colorError: { backgroundColor: t.errorLight, color: t.error },
        colorWarning: { backgroundColor: t.warningLight, color: t.warning },
        colorInfo: { backgroundColor: t.infoLight, color: t.info },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${t.border}`,
          backgroundImage: "none",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${t.border}`,
          backgroundImage: "none",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: 13, minHeight: 36 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 1 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          minHeight: 40,
          fontSize: 13,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 11,
          fontFamily: t.mono,
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: t.border } },
    },
    MuiCheckbox: { defaultProps: { size: "small" } },
    MuiSwitch: { defaultProps: { size: "small" } },
  };
}
